<template>
  <Transition name="fade" mode="out-in">
    <div v-if="usingWorksomeTimesheet">
      <ModalTimesheet
        :hire-id="store.hireGlobalId"
        :worker-id="store.workerId"
        :is-open="timesheetOpen"
        :timesheet-id="timesheetsStore.worksomeTimesheetId"
        :start-date="store.paymentRequestForm.dateStart"
        :end-date="store.paymentRequestForm.dateEnd"
        :rate-type="store.paymentRequestRate.rateType"
        :job-name="store.jobName"
        :company-name="store.companyName"
        :detailed-view="store.hasPayroll && store.paymentRequestRate.rateType === RateType.HOURLY"
        :readonly="store.isEditable === false"
        :enable-timesheet-lines="store.enableWorksomeTimesheetLinesFeature"
        auto-load-timesheet-data
        @updated-timesheet="handleUpdatedTimesheet"
        @cancel="timesheetOpen = false"
      ></ModalTimesheet>
      <ModalTimesheetConfirmationDelete
        :show="isOpenRemoveTimesheetDialog"
        :working="deletingTimesheet"
        @cancel="isOpenRemoveTimesheetDialog = false"
        @delete-timesheet="deleteWorksomeTimesheet"
      />
      <div
        v-if="!timesheetsStore.worksomeTimesheetId"
        class="tw-flex tw-w-full tw-justify-end tw-mt-sm"
      >
        <WdsButton
          default
          class="tw-items-center tw-justify-center"
          data-test="timesheet-create-button"
          @click="timesheetOpen = !timesheetOpen"
        >
          <WdsIcon :size="16" name="plus" />
          <div class="tw-ml-2">{{ $t('Create timesheet') }}</div>
        </WdsButton>
      </div>

      <template v-if="timesheetsStore.worksomeTimesheetId">
        <div class="tw-flex tw-w-full tw-justify-end tw-mt-md">
          <div class="tw-w-[200px] tw-max-w-full tw-flex tw-flex-col tw-gap-md">
            <div class="tw-flex tw-items-center tw-justify-between tw-gap-sm">
              <WdsButton
                secondary
                class="tw-items-center tw-justify-center tw-w-full"
                data-test="timesheet-edit-button"
                @click="timesheetOpen = !timesheetOpen"
              >
                {{ $t('Timesheet attached') }}
              </WdsButton>
              <WdsButtonGhost
                v-if="canDeleteTimesheet"
                data-test="timesheet-delete-button"
                @click="isOpenRemoveTimesheetDialog = true"
              >
                <WdsIcon name="trash-can" :size="16" />
              </WdsButtonGhost>
            </div>
            <div class="tw-flex tw-flex-col tw-gap-2">
              <div class="tw-flex tw-justify-between">
                <WdsText large>{{ $t('Total payable time') }}</WdsText>
                <WdsText large data-test="total-payable-time-from-timesheet">
                  {{ formatFixedDecimal(timesheetFormStore.totalPayableTime) }}
                  {{
                    store.paymentRequestRate.rateType === RateType.DAILY &&
                    timesheetFormStore.useDayInputs
                      ? $t('d (day abbreviation)')
                      : $t('h (hour abbreviation)')
                  }}
                </WdsText>
              </div>
              <div
                v-if="timesheetFormStore.associatedOvertimes.length > 0"
                class="tw-flex tw-justify-between"
              >
                <WdsText>{{ $t('Regular hours') }}</WdsText>
                <WdsText data-test="regular-time-from-timesheet">
                  {{ formatFixedDecimal(timesheetFormStore.totalPayableTimeMinusOvertime) }}
                  {{
                    store.paymentRequestRate.rateType === RateType.DAILY &&
                    timesheetFormStore.useDayInputs
                      ? $t('d (day abbreviation)')
                      : $t('h (hour abbreviation)')
                  }}
                </WdsText>
              </div>
              <div
                v-for="associatedOvertime in timesheetFormStore.associatedOvertimes"
                :key="associatedOvertime.rate_multiplier"
                class="tw-flex tw-justify-between"
              >
                <div class="tw-flex tw-gap-1 tw-items-center">
                  <WdsText>{{ $t('Overtime') }}</WdsText>
                  <WdsTag sm>
                    {{ formatFixedDecimal(associatedOvertime.rate_multiplier) }} x rate
                  </WdsTag>
                  <WdsTooltip v-if="associatedOvertime.contributingPaymentRequests.size > 1" left>
                    <i class="zmdi zmdi-help" />
                    <template #tooltip-content>
                      {{
                        $t(
                          'This overtime has been calculated from time submitted across multiple payment requests:',
                        )
                      }}
                      <span
                        v-for="contributingPaymentRequest in associatedOvertime.contributingPaymentRequests"
                        :key="contributingPaymentRequest"
                      >
                        #{{ contributingPaymentRequest }}
                      </span>
                    </template>
                  </WdsTooltip>
                </div>
                <WdsText>
                  {{ formatFixedDecimal(associatedOvertime.duration) }}
                  {{
                    store.paymentRequestRate.rateType === RateType.DAILY &&
                    timesheetFormStore.useDayInputs
                      ? $t('d (day abbreviation)')
                      : $t('h (hour abbreviation)')
                  }}
                </WdsText>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
    <div v-else-if="usingLegacyTimesheet" class="tw-relative tw-mb-[40px] tw-mt-lg">
      <Spreadsheet
        :disabled="disabled"
        :model-value="currentTimesheet"
        :ratetype="store.paymentRequestRate.rateType"
        @input="updateTimesheetJson"
      ></Spreadsheet>
    </div>
    <div v-else-if="usingFreetextTimesheet" class="tw-mt-lg">
      <WdsInputArea
        v-model="timesheetsStore.timesheetComment"
        data-test="timesheet-text-input"
        :disabled="disabled"
        :placeholder="$t('Report your time here...')"
        :error="validationStore?.v$?.timesheetComment.$errors?.[0]?.$message as string"
      ></WdsInputArea>
    </div>
    <div v-else-if="usingAttachedFileTimesheet" class="tw-mt-lg">
      <BaseFileAttachmentFromOs
        v-model="timesheetsStore.timesheetFiles"
        :disabled="disabled"
        :open-on-mounted="shouldOpenFileSelectorImmediately"
        :default-tags="[$t('Timesheet')]"
        button-class="btn btn-default btn-xs"
      />
    </div>
  </Transition>
  <Transition name="fade" mode="out-in">
    <WdsAlert v-if="showTimeConfirmationInput" warning class="tw-mt-6" bordered>
      <div class="tw-flex tw-items-center tw-gap-sm" data-test="confirm-timesheet">
        <WdsCheckbox
          v-model:checked="store.paymentRequestForm.timesheetConfirmed"
          @update:checked="validationStore?.v$?.timesheetConfirmed.$touch()"
        ></WdsCheckbox>
        {{ $t('I confirm that the hours submitted in this timesheet are correct.') }}
      </div>
    </WdsAlert>
  </Transition>
  <Transition name="fade" mode="out-in">
    <WdsText
      v-if="validationStore?.v$?.timesheetConfirmed.$errors?.[0]?.$message"
      error
      class="tw-mt-2"
    >
      {{ validationStore?.v$?.timesheetConfirmed?.$errors?.[0]?.$message }}
    </WdsText>
  </Transition>
</template>

<script lang="ts" setup>
import {
  WdsAlert,
  WdsButton,
  WdsButtonGhost,
  WdsCheckbox,
  WdsIcon,
  WdsInputArea,
  WdsTag,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import { computed, nextTick, ref, watch } from 'vue'
import moment from 'moment/moment'
import { useMutation } from '@vue/apollo-composable'
import Spreadsheet from '@/components/PaymentRequest/Spreadsheet.vue'
import BaseFileAttachmentFromOs from '@/components/BaseFileAttachmentFromOs.vue'
import { usePaymentRequestStore } from '@/components/PaymentRequest/store/usePaymentRequestStore'
import { TimesheetTypeEnum } from '@/components/PaymentRequest/enums'
import { useTimesheetStore } from '@/components/PaymentRequest/store/useTimesheetStore'
import { useTimesheetFormStore } from '@/components/Timesheet/store/useTimesheetFormStore'
import { useValidationStore } from '@/components/PaymentRequest/store/useValidationStore'
import { useTranslation } from '@/composables/translation'
import ModalTimesheet from '@/components/Timesheet/ModalTimesheet.vue'
import ModalTimesheetConfirmationDelete from '@/components/PaymentRequest/ModalTimesheetConfirmationDelete.vue'
import formatFixedDecimal from '../Timesheet/utilities/formatFixedDecimal'
import { RateType } from '@/enums'
import { graphql } from '@/gql'
import { useHtmlFormStore } from '@/components/PaymentRequest/store/useHtmlFormStore'

const props = withDefaults(
  defineProps<{
    timesheetType: TimesheetTypeEnum
    disabled?: boolean
  }>(),
  {
    disabled: false,
  },
)

const { $t } = useTranslation()
const store = usePaymentRequestStore()
const timesheetsStore = useTimesheetStore()
const timesheetFormStore = useTimesheetFormStore()
const validationStore = useValidationStore()
const htmlFormStore = useHtmlFormStore()

const timesheetOpen = ref(false)

const usingWorksomeTimesheet = computed(() => props.timesheetType === TimesheetTypeEnum.TIMESHEET)
const usingLegacyTimesheet = computed(() => props.timesheetType === TimesheetTypeEnum.SHEET)
const usingFreetextTimesheet = computed(() => props.timesheetType === TimesheetTypeEnum.TEXT)
const usingAttachedFileTimesheet = computed(() => props.timesheetType === TimesheetTypeEnum.FILE)

const currentTimesheet = computed(() => {
  let json = [[moment().format('YYYY-MM-DD'), '', '']]
  try {
    json = JSON.parse(timesheetsStore?.timesheetSpreadsheetValue)
  } catch (exception) {
    //
  }
  return json
})
const updateTimesheetJson = (json) => {
  // BaseTimesheet emits input with updated json string, OR on actual input
  // We don't want to handle native input events
  if (typeof json === 'string') {
    timesheetsStore.timesheetSpreadsheetValue = json
  }
}

const shouldOpenFileSelectorImmediately = ref(false)
watch(
  () => props.timesheetType,
  (newValue, oldValue) => {
    shouldOpenFileSelectorImmediately.value =
      oldValue !== TimesheetTypeEnum.FILE &&
      newValue === TimesheetTypeEnum.FILE &&
      timesheetsStore.timesheetFiles.length === 0
  },
)

const isOpenRemoveTimesheetDialog = ref(false)

const { mutate: deleteTimesheet, loading: deletingTimesheet } = useMutation(
  graphql(/* GraphQL */ `
    mutation deleteTimesheet($input: DeleteTimesheetInput!) {
      deleteTimesheet(input: $input) {
        id
      }
    }
  `),
  () => ({
    variables: {
      input: {
        id: timesheetsStore.worksomeTimesheetId,
      },
    },
    clientId: 'publicApi',
  }),
)

const deleteWorksomeTimesheet = async () => {
  await deleteTimesheet()
  timesheetFormStore.rollbackTimesheetRegistrations()
  timesheetFormStore.clearTimesheet()
  isOpenRemoveTimesheetDialog.value = false
  timesheetsStore.worksomeTimesheetId = ''
  store.paymentRequestForm.timesheetConfirmed = false
}
// If editing an existing payment request's timesheet, we must auto-save the payment request
// so that the payment request totals are updated on backend since a user could potentially
// exit the payment request before saving it themselves, leaving PR and Timesheet out of sync
const shouldAutoSavePaymentRequestOnTimesheetUpdates = computed(
  () => store.paymentRequestId && store.isEditable,
)
const handleUpdatedTimesheet = async (timesheetId) => {
  timesheetsStore.worksomeTimesheetId = timesheetId
  if (shouldAutoSavePaymentRequestOnTimesheetUpdates.value) {
    if (!store.isDraft) {
      store.paymentRequestForm.timesheetConfirmed = true
    }
    await nextTick()
    htmlFormStore.submitForm(
      // @ts-ignore
      store.isDraft ? '/bills/draft' : `/bills?redirect_uri=/bills/${store.paymentRequestId}/edit`,
    )
  } else {
    timesheetOpen.value = false
    store.paymentRequestForm.timesheetConfirmed = false
  }
}
// We only allow deletion of timesheets if the payment request is editable
// and the payment request has not yet been created. Otherwise, the payment request total will
// still have the amount of hours as the deleted timesheet
const canDeleteTimesheet = computed(() => store.isEditable && !store.paymentRequestId)

const showTimeConfirmationInput = computed(
  () =>
    store.isEditable &&
    timesheetsStore.requireTimesheetConfirmation &&
    props.timesheetType !== TimesheetTypeEnum.NONE,
)
</script>

<style scoped lang="scss">
:deep(.jexcel) {
  width: 100% !important;
}
:deep(.wds-checkbox-control) {
  path {
    stroke: #fff !important;
  }
}
</style>
